import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import {
  DashboardLayout,
  MainContent,
  Sidebar,
} from 'components/DashboardLayout';
import DataTable, { CellPrimary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import PageContainer from 'components/PageContainer';
import {
  HeaderContainer,
  HeaderTitle,
  HeaderSubtitle,
} from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import TableFilter from 'components/TableFilter';
import Toaster from 'components/Toaster';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { formatTimestamp } from 'shared/formatters';
import { useGetAnnualPurchaseListWithDates } from './gql/useGetAnnualPurchaseListWithDates';
import useAnnualPurchasesExport from './hooks/useAnnualPurchasesExport';

const TrackAnnualPurchaseDetail = () => {
  const { id } = useParams();
  const { pathname, search } = useLocation();
  const history = useHistory();

  // Parse query params
  const searchParams = new URLSearchParams(search);
  const ticketName = searchParams.get('ticketName') || 'Annual Ticket';
  const accountName = searchParams.get('accountName') || '';
  const accountId = searchParams.get('account_id') || '';

  // Determine current context for routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // Filter state - pending (shown in filter panel) vs applied (used in query)
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  const [purchases, setPurchases] = useState([]);
  const [count, setCount] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Export hook
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useAnnualPurchasesExport();

  // Main data query
  const { data, loading } = useGetAnnualPurchaseListWithDates(
    id,
    appliedStartDate,
    appliedEndDate,
    input?.queryString,
    currentPage
  );

  useEffect(() => {
    if (data?.getAnnualPurchaseList) {
      setPurchases(data.getAnnualPurchaseList.results || []);
      setCount(data.getAnnualPurchaseList.count || 0);
    }
  }, [data]);

  // Handle search
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  // Handle pagination
  const handlePageChange = (page) => {
    const totalPages = Math.ceil(count / 15);
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle filter change - updates pending state
  const handleFilterChange = (newFilters) => {
    if (newFilters.startDate !== undefined) {
      setPendingStartDate(newFilters.startDate || '');
    }
    if (newFilters.endDate !== undefined) {
      setPendingEndDate(newFilters.endDate || '');
    }
  };

  // Handle filter apply - updates applied state to trigger query
  const handleApplyFilters = (newFilters) => {
    const newStartDate = newFilters.startDate ?? pendingStartDate;
    const newEndDate = newFilters.endDate ?? pendingEndDate;

    setAppliedStartDate(newStartDate);
    setAppliedEndDate(newEndDate);
    setPendingStartDate(newStartDate);
    setPendingEndDate(newEndDate);

    updateQuery({ page: 1 });
  };

  // Handle filter clear
  const handleClearFilters = () => {
    setAppliedStartDate('');
    setAppliedEndDate('');
    setPendingStartDate('');
    setPendingEndDate('');
    updateQuery({ page: 1 });
  };

  // Handle export
  const handleExport = useCallback(
    async (format) => {
      setShowExportModal(false);

      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        const params = {
          ticketId: id,
          accountId: accountId || null,
          search: input.queryString,
          startDate: appliedStartDate,
          endDate: appliedEndDate,
        };

        const jobId = await startExport(params, format);

        subscribeToProgress(jobId, {
          onProgress: (status) => {
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} purchases (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              const downloadUrl = await getDownloadUrl(jobId);

              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } purchases exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export annual purchases';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        setToasterData({
          title: 'Export Failed',
          message: 'Failed to start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      id,
      accountId,
      input.queryString,
      appliedStartDate,
      appliedEndDate,
      startExport,
      subscribeToProgress,
      getDownloadUrl,
    ]
  );

  // Build filter configuration for TableFilter
  const filterConfig = [
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'date',
      placeholder: 'Select Start Date',
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'date',
      placeholder: 'Select End Date',
    },
  ];

  // Current filter values for TableFilter
  const filterValues = {
    startDate: pendingStartDate,
    endDate: pendingEndDate,
  };

  // DataTable columns
  const columns = [
    {
      field: 'purchase_date',
      header: 'Purchase Date',
      render: (purchase) => (
        <CellPrimary>
          {formatTimestamp(
            Number(purchase.purchase_date),
            'MMM DD - YYYY h:mm A',
            true
          ).toUpperCase()}
        </CellPrimary>
      ),
    },
    {
      field: 'user',
      header: 'Name',
      mobileTitle: true,
      render: (purchase) => (
        <CellPrimary>{purchase.user?.formatted_name || '-'}</CellPrimary>
      ),
    },
    {
      field: 'total',
      header: 'Cost',
    },
  ];

  // DataTable actions
  const actions = [
    {
      type: 'view',
      title: 'View Transaction',
      mobileLabel: 'View',
      to: (purchase) => `${routePrefix}/transactions/detail/${purchase.id}`,
    },
  ];

  const totalPages = Math.ceil(count / 15);

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="events" />
            <div>
              <HeaderTitle>{ticketName}</HeaderTitle>
              <HeaderSubtitle>
                {accountName || 'All Accounts'} - Annual Purchases
              </HeaderSubtitle>
            </div>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout with Sidebar */}
        <DashboardLayout>
          <MainContent>
            {/* Content Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Page Header with Search and Filters */}
                <PageToolbar>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search by last name..."
                    debounceMs={500}
                  />
                  <TableFilter
                    filters={filterConfig}
                    values={filterValues}
                    onChange={handleFilterChange}
                    onApply={handleApplyFilters}
                    onClear={handleClearFilters}
                  />
                </PageToolbar>

                {/* DataTable */}
                <DataTable
                  columns={columns}
                  data={purchases}
                  loading={loading}
                  keyField="id"
                  emptyMessage="No purchases found"
                  emptySubtext="Try adjusting your search or filters"
                  actions={actions}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Back to Purchases',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
                      />
                    </svg>
                  ),
                  onClick: () => history.push(`${routePrefix}/annualPurchases`),
                },
                {
                  label: 'Export Report',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                    </svg>
                  ),
                  onClick: () => setShowExportModal(true),
                  disabled: isExporting || count === 0,
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
          recordCount={count}
          dataType="annual purchases"
        />

        {/* Toaster for export notifications */}
        <Toaster
          isOpen={!!toasterData}
          title={toasterData?.title || ''}
          message={toasterData?.message}
          type={toasterData?.type}
          persistent={toasterData?.persistent}
          action={toasterData?.action}
          onClose={() => setToasterData(null)}
        />
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackAnnualPurchaseDetail;
